/**
 * CanonicalValidation - runtime validation for canonical entity shapes.
 * date_of_change: 2025-09-23
 * purpose: Validate that entities entering combat expose canonical top-level fields
 * without performing transformations (zero-transformation beyond ingestion).
 *
 * Validates (2024 rules-aligned engine contract):
 * - Top-level hp/maxHp numbers OR hpObj { current, max }
 * - Top-level ac number OR ac.base number
 * - Top-level speed number (feet/round)
 * - abilities with long keys { strength, dexterity, constitution, intelligence, wisdom, charisma }
 * - equipment.items as array (equipment.other may exist only as ingest alias)
 */

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings?: string[];
}

export function validateEntity(entity: any): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Name required for logging clarity
  if (!entity || typeof entity !== 'object') {
    errors.push('Entity is not an object');
    return { valid: false, errors, warnings };
  }

  // HP validation
  const hasTopHp = typeof entity.hp === 'number';
  const hasTopMax = typeof entity.maxHp === 'number';
  const hasHpObj = entity.hpObj && typeof entity.hpObj.current === 'number' && typeof entity.hpObj.max === 'number';
  if (!(hasHpObj || (hasTopHp && hasTopMax))) {
    errors.push('Missing canonical HP fields: need hp+maxHp numbers or hpObj { current, max }');
  }

  // AC validation
  const hasAcNumber = typeof entity.ac === 'number';
  const hasAcBase = entity.ac && typeof entity.ac.base === 'number';
  if (!(hasAcNumber || hasAcBase)) {
    errors.push('Missing canonical AC: expected ac (number) or ac.base (number)');
  }

  // Speed validation (characters) - monsters may have speeds map, engine prefers numeric speed for characters
  if (entity.type !== 'monster') {
    if (entity.speed != null && typeof entity.speed !== 'number') {
      warnings.push('Character speed is not a number; engine expects numeric feet/round');
    }
  }

  // Abilities validation
  const abil = entity.abilities;
  const hasLongAbilities = abil && ['strength','dexterity','constitution','intelligence','wisdom','charisma'].every(k => typeof abil[k] === 'number');
  if (!hasLongAbilities) {
    errors.push('abilities must include long keys: strength,dexterity,constitution,intelligence,wisdom,charisma');
  }

  // Equipment container validation
  const equip = entity.equipment;
  if (equip && !Array.isArray(equip.items)) {
    if (Array.isArray(equip.other)) {
      warnings.push('equipment.other present; prefer equipment.items (ingest-only alias)');
    } else {
      warnings.push('equipment.items missing or not an array');
    }
  }

  return { valid: errors.length === 0, errors, warnings };
}
